import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { AnyPage, Profile } from 'api/api.types'
import type { RootState } from './index'

type AppState = {
  selectedCityId: number | null,
  page: AnyPage | null,
  isPageLoading: boolean,
  /** True on the first visit to the site, used to show the city picker */
  isFirstStart: boolean,
  authToken: string | null,
  profile: Profile | null,
}

const initialState: AppState = {
  selectedCityId: null,
  page: null,
  isPageLoading: false,
  isFirstStart: false,
  authToken: null,
  profile: null,
}

const slice = createSlice({
  name: 'appState',
  initialState,
  reducers: {
    setSelectedCityId: (state, action: PayloadAction<number | null>) => {
      state.selectedCityId = action.payload
    },
    setPage: (state, action: PayloadAction<AnyPage | null>) => {
      state.page = action.payload
    },
    setIsPageLoading: (state, action: PayloadAction<boolean>) => {
      state.isPageLoading = action.payload
    },
    setIsFirstStart: (state, action: PayloadAction<boolean>) => {
      state.isFirstStart = action.payload
    },
    setAuthToken: (state, action: PayloadAction<string | null>) => {
      state.authToken = action.payload
    },
    setProfile: (state, action: PayloadAction<Profile | null>) => {
      state.profile = action.payload
    },
    updateProfileField: <K extends keyof Profile>(
      state: AppState,
      action: PayloadAction<{ field: K, value: Profile[K] }>,
    ) => {
      if (state.profile) {
        state.profile[action.payload.field] = action.payload.value
      }
    },
    signOut: (state) => {
      state.authToken = null
      state.profile = null
    },
  },
})

export const {
  setSelectedCityId,
  setPage,
  setIsPageLoading,
  setIsFirstStart,
  setAuthToken,
  setProfile,
  updateProfileField,
  signOut,
} = slice.actions

export const appStateReducer = slice.reducer

export const getSelectedCityId = (state: RootState) => state.appState.selectedCityId
export const getPage = (state: RootState) => state.appState.page
export const getIsPageLoading = (state: RootState) => state.appState.isPageLoading
export const getIsFirstStart = (state: RootState) => state.appState.isFirstStart
export const getAuthToken = (state: RootState) => state.appState.authToken
export const getIsAuthenticated = (state: RootState) => state.appState.authToken !== null
export const getProfile = (state: RootState) => state.appState.profile
